import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Tabs,
  Tab,
  Button,
  TextField,
  IconButton,
  MenuItem,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddLanguageModal from "./AddLanguageModal";
import UnsavedChangesDialog from "../../UnsavedChangesDialog/UnsavedChangesDialog";
import { useDispatch } from "react-redux";
import { patchLegalDocumentInfos } from "../../../Services/redux/LocalesRedux/LegallocaleDocumentsActions";

const documentTypes = [
  { label: "Terms of use", value: "Terms of use" },
  { label: "Privacy policy", value: "Privacy policy" },
  { label: "Imprint", value: "Imprint" },
];

const cloneData = (data) => {
  return structuredClone(data);
};

const getLocalizedTitle = (doc, language) => doc.localizedTitles?.[language] || "";

const getCompleteDocuments = (documents) => {
  return documents.filter((doc) => {
    const hasType = doc.type && doc.type.trim() !== "";
    const hasAnyTitle = Object.values(doc.localizedTitles || {}).some(
      (title) => title && title.trim() !== ""
    );
    return hasType && hasAnyTitle;
  });
};

const alignTitlesForLanguage = (documents, currentLanguage) => {
  return documents.map((doc) => ({
    ...doc,
    title: getLocalizedTitle(doc, currentLanguage),
  }));
};

const removeLanguagesFromDocuments = (docs, languagesToRemove) => {
  return docs
    .map((doc) => {
      if (!doc.localizedTitles) {
        return doc;
      }

      const cleanedLocalizedTitles = { ...doc.localizedTitles };
      languagesToRemove.forEach((langCode) => {
        delete cleanedLocalizedTitles[langCode];
      });

      if (Object.keys(cleanedLocalizedTitles).length === 0) {
        return null;
      }

      return {
        ...doc,
        localizedTitles: cleanedLocalizedTitles,
      };
    })
    .filter((doc) => doc !== null);
};

const sortLanguages = (languageArray) => {
  languageArray.sort((a, b) => {
    if (a.default && !b.default) return -1;
    if (!a.default && b.default) return 1;
    return 0;
  });
};

export default function LegalDocumentationForm({
  locales,
  legalDocumentInfos,
  callLegalDocInfo,
}) {
  const dispatch = useDispatch();
  const [activeLang, setActiveLang] = useState(0);
  const [documents, setDocuments] = useState([{ type: "", title: "" }]);
  const [languages, setLanguages] = useState([
    { value: "en_US", default: true },
  ]);
  const [openAddLanguageModal, setOpenAddLanguageModal] = useState(false);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const [originalDocuments, setOriginalDocuments] = useState([]); // Store original state for cancel
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const tabsContainerRef = React.useRef(null);

  const handleTabChange = (event, newValue) => {
    setActiveLang(newValue);

    // Update document titles based on selected language
    if (languages[newValue]) {
      const selectedLanguage = languages[newValue].value;

      setDocuments((docs) =>
        docs.map((doc) => ({
          ...doc,
          title:
            doc.localizedTitles?.[selectedLanguage] || "", // Show empty if no translation found for this language
        }))
      );
    }
  };

  // Check if tabs container needs scroll buttons
  useEffect(() => {
    const checkScrollNeeded = () => {
      // Always show scroll buttons if there are more than 4 languages
      if (languages.length > 4) {
        setShowScrollButtons(true);
        return;
      }

      if (tabsContainerRef.current) {
        const container = tabsContainerRef.current;
        // Get the actual tabs content
        const tabsElement = container.querySelector(".MuiTabs-root");
        if (tabsElement) {
          const isScrollable = tabsElement.scrollWidth > container.clientWidth;
          setShowScrollButtons(isScrollable);
        }
      }
    };

    // Delay check to ensure tabs are rendered
    const timeoutId = setTimeout(checkScrollNeeded, 100);

    checkScrollNeeded();
    window.addEventListener("resize", checkScrollNeeded);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkScrollNeeded);
    };
  }, [languages]);

  // Scroll tabs left
  const scrollTabsLeft = () => {
    if (tabsContainerRef.current) {
      const container = tabsContainerRef.current;
      const tabsElement = container.querySelector(".MuiTabs-scroller");
      if (tabsElement) {
        const scrollAmount = 200;
        tabsElement.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      }
    }
  };

  // Scroll tabs right
  const scrollTabsRight = () => {
    if (tabsContainerRef.current) {
      const container = tabsContainerRef.current;
      const tabsElement = container.querySelector(".MuiTabs-scroller");
      if (tabsElement) {
        const scrollAmount = 200;
        tabsElement.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    }
  };

  // Update default language whenever documents change
  useEffect(() => {
    // Find the first language that has a document with both type and title
    let defaultLanguageValue = "en_US"; // Default fallback

    for (const lang of languages) {
      const hasCompleteDocument = documents.some((doc) => {
        const hasTitle = doc.localizedTitles?.[lang.value]?.trim();
        const hasType = doc.type?.trim();
        return hasTitle && hasType;
      });

      if (hasCompleteDocument) {
        defaultLanguageValue = lang.value;
        break; // Use the first language found with complete documents
      }
    }

    // Only update if the default language actually changed
    const currentDefault = languages.find((lang) => lang.default);
    if (currentDefault?.value !== defaultLanguageValue) {
      setLanguages((langs) =>
        langs.map((lang) => ({
          ...lang,
          default: lang.value === defaultLanguageValue,
        }))
      );
    }
  }, [documents, languages]);

  // Get documents to display for current language
  const getDocumentsToDisplay = () => {
    if (!languages[activeLang]) return [];

    const currentLang = languages[activeLang].value;

    if (isEditMode) {
      // In edit mode, show documents that have any content for the current language
      // This includes complete, partial, and newly created documents
      const documentsForCurrentLang = [];

      documents.forEach((doc, originalIndex) => {
        // Check if this document has content for current language
        const hasTitle = doc.localizedTitles?.[currentLang]?.trim();
        const hasType = doc.type?.trim();

        // Show documents that have:
        const hasCompleteContent = hasTitle && hasType;
        const hasTitleForCurrentLang = hasTitle; // Only show if has title for current language
        const hasPlaceholderForCurrentLang =
          doc.localizedTitles?.[currentLang] !== undefined;
        const isNewForCurrentLang =
          doc.id?.toString().startsWith(`new_${currentLang}_`);

        // Only show documents that specifically belong to or were created for the current language
        if (
          hasCompleteContent ||
          hasTitleForCurrentLang ||
          hasPlaceholderForCurrentLang ||
          isNewForCurrentLang
        ) {
          documentsForCurrentLang.push({
            ...doc,
            originalIndex,
            title: doc.localizedTitles?.[currentLang] || "",
          });
        }
      });

      return documentsForCurrentLang;
    } else {
      // In view mode, show only documents that have complete content for the current language
      const documentsWithContentForLang = [];

      documents.forEach((doc, originalIndex) => {
        // Check if this document has both type and title for current language
        const hasTitle = doc.localizedTitles?.[currentLang]?.trim();
        const hasType = doc.type?.trim();

        if (hasTitle && hasType) {
          documentsWithContentForLang.push({
            ...doc,
            originalIndex,
            title: doc.localizedTitles[currentLang],
          });
        }
      });

      return documentsWithContentForLang;
    }
  };

  const handleDocChange = (displayIndex, field, value) => {
    setDocuments((docs) => {
      const updated = [...docs];

      // Get the documents to display to find the original index
      const documentsToDisplay = getDocumentsToDisplay();
      const docToUpdate = documentsToDisplay[displayIndex];

      // Use originalIndex if available, otherwise use displayIndex for new documents
      const actualIndex =
        docToUpdate?.originalIndex === undefined
          ? displayIndex
          : docToUpdate.originalIndex;

      // Ensure we have a valid document at the actual index
      if (actualIndex >= updated.length) {
        // This is a new document, add it to the array
        updated.push({
          id: `new_${Date.now()}`,
          type: "",
          title: "",
          localizedTitles: {},
        });
      }

      updated[actualIndex][field] = value;

      // If updating title, also update the localizedTitles for current language
      if (field === "title" && languages[activeLang]) {
        const currentLang = languages[activeLang].value;
        if (!updated[actualIndex].localizedTitles) {
          updated[actualIndex].localizedTitles = {};
        }
        updated[actualIndex].localizedTitles[currentLang] = value;
      }

      // Check for duplicates with the updated documents array
      const duplicateCheck = checkForDuplicates(updated);

      if (duplicateCheck.hasDuplicates) {
        const duplicate = duplicateCheck.duplicates[0]; // Show first duplicate
        const errorMsg = duplicate.message;
        setDuplicateError(errorMsg);
      } else {
        setDuplicateError(null);
      }

      return updated;
    });
  };

  const handleAddDoc = () => {
    if (!languages[activeLang]) return;

    const currentLang = languages[activeLang].value;
    const newDoc = {
      id: `new_${currentLang}_${Date.now()}`, // Generate language-specific ID
      type: "",
      title: "",
      localizedTitles: {
        [currentLang]: "", // Initialize with empty string for current language
      },
    };
    setDocuments((docs) => [...docs, newDoc]);
  };

  const handleRemoveDoc = (displayIndex) => {
    if (!languages[activeLang]) return;

    const currentLang = languages[activeLang].value;

    setDocuments((docs) => {
      // Get the documents to display to find the original index
      const documentsToDisplay = getDocumentsToDisplay();
      const docToRemove = documentsToDisplay[displayIndex];

      if (!docToRemove) return docs;

      // Use originalIndex if available
      const actualIndex =
        docToRemove.originalIndex === undefined
          ? displayIndex
          : docToRemove.originalIndex;
      const updated = [...docs];
      const docToUpdate = updated[actualIndex];

      if (!docToUpdate) return docs;

      let finalUpdatedDocs;

      // Check if this document has content for other languages
      const hasOtherLanguages =
        docToUpdate.localizedTitles &&
        Object.keys(docToUpdate.localizedTitles).some(
          (lang) => lang !== currentLang
        );

      if (hasOtherLanguages) {
        // Remove only the current language from localizedTitles
        const updatedLocalizedTitles = { ...docToUpdate.localizedTitles };
        delete updatedLocalizedTitles[currentLang];

        // Find the first remaining language to display as title
        const remainingLanguages = Object.keys(updatedLocalizedTitles);
        const fallbackTitle =
          remainingLanguages.length > 0
            ? updatedLocalizedTitles[remainingLanguages[0]]
            : "";

        updated[actualIndex] = {
          ...docToUpdate,
          localizedTitles: updatedLocalizedTitles,
          title: fallbackTitle,
        };

        finalUpdatedDocs = updated;
      } else {
        // Remove the entire document if it only exists for the current language
        finalUpdatedDocs = docs.filter((_, i) => i !== actualIndex);
      }

      // Check for duplicates with the updated documents array after removal
      const duplicateCheck = checkForDuplicates(finalUpdatedDocs);

      if (duplicateCheck.hasDuplicates) {
        const duplicate = duplicateCheck.duplicates[0]; // Show first duplicate
        const errorMsg = duplicate.message;
        setDuplicateError(errorMsg);
      } else {
        setDuplicateError(null);
      }

      return finalUpdatedDocs;
    });
  };

  const handleAddLanguageClick = () => {
    setOpenAddLanguageModal(true);
  };

  const handleAddLanguageModalClose = () => {
    setOpenAddLanguageModal(false);
  };

  const handleAddLanguageSave = () => {
    // Here you would typically add the new languages to your language list
    // For now, we'll just close the modal
    setOpenAddLanguageModal(false);
  };

  const handleManageDocument = () => {
    // Store the current documents state before entering edit mode (deep copy)
    // Only store complete documents as the baseline
    const completeDocuments = getCompleteDocuments(documents);

    // Ensure titles are properly aligned with current language before saving snapshot
    const currentLanguage = languages[activeLang]?.value || "en_US";
    const documentsWithAlignedTitles = alignTitlesForLanguage(
      completeDocuments,
      currentLanguage
    );

    const currentDocumentsSnapshot = cloneData(documentsWithAlignedTitles);
    setOriginalDocuments(currentDocumentsSnapshot);

    // Set documents to only complete documents when entering edit mode
    setDocuments(currentDocumentsSnapshot);

    setIsEditMode(true);
    setDuplicateError(false);
  };

  // Define handleSave function
  const handleSave = useCallback(() => {
    if (!languages[activeLang]) {
      console.error("No active language selected");
      return;
    }

    const currentLocale = languages[activeLang].value;

    // Filter out documents that have both type and title for the current language
    const validDocuments = documents.filter((doc) => {
      const hasTitle = doc.localizedTitles?.[currentLocale]?.trim() !== "";
      const hasType = doc.type && doc.type.trim() !== "";
      return hasTitle && hasType;
    });

    // Only make API call if there are valid documents
    if (validDocuments.length > 0) {
      // Create the data structure as requested
      const saveData = {
        portfolioId: sessionStorage.getItem("portfolioId"), // You might want to make this dynamic
        locale: currentLocale,
        documentTypes: validDocuments.map((doc) => ({
          documentType: doc.type,
          documentTitle: doc.localizedTitles[currentLocale],
          isEnabled: true,
        })),
      };

      dispatch(patchLegalDocumentInfos(saveData, currentLocale));
    }

    // Exit edit mode after saving
    setIsEditMode(false);
    setDuplicateError(false);

    // Update original documents state to only include complete documents
    // This ensures incomplete documents don't persist when re-entering edit mode
    const completeDocuments = getCompleteDocuments(documents);

    setOriginalDocuments(cloneData(completeDocuments));

    // Also update the current documents state to only show complete documents in view mode
    setDocuments(cloneData(completeDocuments));
  }, [languages, activeLang, documents, dispatch]);

  const handleCancel = () => {
    // Always show the unsaved changes dialog when cancel is clicked

    setShowUnsavedChangesDialog(true);
  };

  const performCancel = () => {
    setIsEditMode(false);
    setDuplicateError(false);
    setShowUnsavedChangesDialog(false);
    callLegalDocInfo();
    // Restore the original documents from when edit mode was entered
    const restoredDocuments = cloneData(originalDocuments);

    // Ensure titles are aligned with the currently active language
    const currentLanguage = languages[activeLang]?.value || "en_US";
    const documentsWithAlignedTitles = alignTitlesForLanguage(
      restoredDocuments,
      currentLanguage
    );

    setDocuments(documentsWithAlignedTitles);
  };

  const handleSaveChanges = () => {
    // Call the save function

    handleSave();
    setShowUnsavedChangesDialog(false);
  };

  const addRemoveLanguage = (languageArray) => {
    // Always ensure en_US is included as the default language
    const hasEnUS = languageArray.some((lang) => lang.value === "en_US");
    if (!hasEnUS) {
      languageArray.unshift({ value: "en_US", default: true });
    }

    // Check for en_US with clearDocuments flag before cleaning up the array
    const enUSLanguage = languageArray.find((lang) => lang.value === "en_US");
    const shouldClearEnUSDocuments = enUSLanguage?.clearDocuments;

    // Ensure en_US is marked as default and clean up clearDocuments flags
    languageArray = languageArray.map((lang) => ({
      value: lang.value,
      default: lang.value === "en_US",
    }));

    // Sort to put en_US first
    sortLanguages(languageArray);

    // Handle en_US document clearing if requested
    if (shouldClearEnUSDocuments) {
      const clearEnUSData = {
        portfolioId: sessionStorage.getItem("portfolioId"),
        locale: "en_US",
        documentTypes: [],
      };

      dispatch(patchLegalDocumentInfos(clearEnUSData, "en_US"));

      // Clear en_US documents from local state
      setDocuments((docs) => removeLanguagesFromDocuments(docs, ["en_US"]));
    }

    // Find removed languages
    const existingLanguageCodes = languages.map((lang) => lang.value);
    const newLanguageCodes = new Set(languageArray.map((lang) => lang.value));
    const removedLanguages = existingLanguageCodes.filter(
      (langCode) => !newLanguageCodes.has(langCode)
    );

    // For each removed language, call patchLegalDocumentInfos with empty documentTypes
    removedLanguages.forEach((removedLangCode) => {
      // Skip currently active language (they shouldn't be removable anyway)
      // Note: en_US removal is now handled above as document clearing, not language removal
      if (removedLangCode !== languages[activeLang]?.value) {
        const removeData = {
          portfolioId: sessionStorage.getItem("portfolioId"),
          locale: removedLangCode,
          documentTypes: [],
        };

        dispatch(patchLegalDocumentInfos(removeData, removedLangCode));
      }
    });

    // Clean up localizedTitles from existing documents for removed languages
    if (removedLanguages.length > 0) {
      setDocuments((docs) => removeLanguagesFromDocuments(docs, removedLanguages));
    }

    // Check if new languages have been added
    if (languageArray.length > languages.length) {
      // Update languages first
      setLanguages(languageArray);

      // If we have existing documents, make sure they have empty entries for new languages
      // If no documents exist, create an empty document to show the form
      if (documents.length === 0) {
        setDocuments([
          {
            id: `new_${Date.now()}`,
            type: "",
            title: "",
            localizedTitles: {},
          },
        ]);
      }
    } else {
      // Check if the currently active language was removed
      const currentActiveLangCode = languages[activeLang]?.value;
      if (
        currentActiveLangCode &&
        removedLanguages.includes(currentActiveLangCode)
      ) {
        // Switch to en_US tab (which should be at index 0)
        setActiveLang(0);
      }

      // Just update languages (including case where languages were removed)
      setLanguages(languageArray);
    }
  };

  // Check if there are documents available for current language
  const hasDocumentsForCurrentLanguage = () => Boolean(languages[activeLang]);

  // Count total documents (complete + incomplete) for the current language
  const getCurrentLanguageTotalDocumentCount = () => {
    if (!languages[activeLang]) return 0;

    const currentLang = languages[activeLang].value;
    return documents.filter((doc) => {
      // Check if document has any content for current language (complete or incomplete)
      const hasTitle = doc.localizedTitles?.[currentLang]?.trim();
      const hasPlaceholderForCurrentLang =
        doc.localizedTitles?.[currentLang] !== undefined;
      const isNewForCurrentLang =
        doc.id?.toString().startsWith(`new_${currentLang}_`);

      // Only count documents that specifically belong to the current language
      return hasTitle || hasPlaceholderForCurrentLang || isNewForCurrentLang;
    }).length;
  };

  // Check for duplicate document types and duplicate titles within the same document type for current language
  const checkForDuplicates = (docsToCheck = documents) => {
    if (!languages[activeLang]) return { hasDuplicates: false, duplicates: [] };

    const currentLang = languages[activeLang].value;
    const duplicates = [];
    const documentTypeMap = new Map(); // Track document types
    const titleTypeMap = new Map(); // Track title-type combinations

    // Only check documents that have non-empty types
    const validDocs = docsToCheck.filter(
      (doc) => doc.type && doc.type.trim() !== ""
    );

    validDocs.forEach((doc, originalIndex) => {
      const docType = doc.type.trim();

      // Check for duplicate document types (only if document has both type and title)
      const title = doc.localizedTitles?.[currentLang]?.trim() || "";
      const hasTitle = title !== "";

      if (hasTitle) {
        // Only check for duplicate types if this document has a title
        if (documentTypeMap.has(docType)) {
          const existingIndex = documentTypeMap.get(docType);
          duplicates.push({
            index: originalIndex,
            existingIndex,
            type: docType,
            title: "",
            isDuplicateType: true,
            message: `Document type "${docType}" already exists for this language`,
          });
        } else {
          documentTypeMap.set(docType, originalIndex);
        }

        // Check for duplicate titles within the same document type
        const key = `${docType}-${title}`;
        if (titleTypeMap.has(key)) {
          // Found duplicate title within same type
          const existingIndex = titleTypeMap.get(key);
          duplicates.push({
            index: originalIndex,
            existingIndex,
            type: docType,
            title: title,
            isDuplicateTitle: true,
            message: `Duplicate title "${title}" found for document type "${docType}"`,
          });
        } else {
          titleTypeMap.set(key, originalIndex);
        }
      }
    });

    return {
      hasDuplicates: duplicates.length > 0,
      duplicates,
    };
  };

  useEffect(() => {
    // Extract all languages from localizedTitles where enabled is true
    const extractedLanguages = new Set();
    const enabledDocuments = [];

    if (legalDocumentInfos?.length > 0) {
      legalDocumentInfos.forEach((item) => {
        if (item.enabled && item.localizedTitles) {
          // Get all language keys from localizedTitles
          Object.keys(item.localizedTitles).forEach((languageKey) => {
            extractedLanguages.add(languageKey);
          });

          // Store enabled documents with their data
          enabledDocuments.push({
            id: item.id,
            type: item.documentType,
            localizedTitles: item.localizedTitles,
          });
        }
      });

      // Convert Set to Array and create language objects
      let languageArray = Array.from(extractedLanguages).map((langKey) => ({
        value: langKey,
        default: langKey === "en_US",
      }));

      // Always ensure en_US is included as the default language
      const hasEnUS = languageArray.some((lang) => lang.value === "en_US");
      if (!hasEnUS) {
        languageArray.unshift({ value: "en_US", default: true });
      }

      // Sort array to put default language (en_US) first
      sortLanguages(languageArray);

      // If no languages found at all, default to en_US
      if (languageArray.length === 0) {
        languageArray.push({ value: "en_US", default: true });
      }

      setLanguages(languageArray);

      // Set documents if any enabled documents found
      if (enabledDocuments.length > 0) {
        setDocuments(
          enabledDocuments.map((doc) => ({
            id: doc.id,
            type: doc.type,
            title: doc.localizedTitles?.["en_US"] || "", // Default to en_US title
            localizedTitles: doc.localizedTitles,
          }))
        );
      }
    }
  }, [legalDocumentInfos]);

  return (
    <Box
      sx={{
        paddingLeft: "18px",
        background: "#fff",
        maxWidth: 908,
        mx: "auto",
      }}
    >
      {/* Header with Title and Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Legal Documentation
        </Typography>

        {isEditMode ? (
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              sx={{ textTransform: "none", minWidth: 80 }}
              variant="outlined"
              color="inherit"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              sx={{ textTransform: "none", minWidth: 80 }}
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={checkForDuplicates().hasDuplicates}
            >
              Save
            </Button>
          </Box>
        ) : (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleManageDocument}
            sx={{
              minWidth: "fit-content",
              textTransform: "none",
              borderColor: "rgba(42, 44, 45, 1)",
              color: "rgba(42, 44, 45, 1)",
              "&:hover": {
                borderColor: "rgba(42, 44, 45, 0.8)",
                backgroundColor: "rgba(42, 44, 45, 0.04)",
              },
            }}
          >
            Manage Document
          </Button>
        )}
      </Box>

      {/* Tabs Row - Always visible */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 2,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            maxWidth: isEditMode ? "calc(100% - 150px)" : "100%",
          }}
        >
          {/* Left scroll button */}
          {showScrollButtons && (
            <IconButton
              onClick={scrollTabsLeft}
              sx={{
                mr: 1,
                minWidth: 32,
                height: 32,
                border: "1px solid #e0e0e0",
                backgroundColor: "#f5f5f5",
                "&:hover": { backgroundColor: "#e0e0e0" },
                flexShrink: 0,
              }}
              size="small"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}

          {/* Tabs container with horizontal scroll */}
          <Box
            ref={tabsContainerRef}
            sx={{
              flex: 1,
              overflowX: "hidden",
              overflowY: "hidden",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <Tabs
              value={activeLang}
              onChange={(event, newValue) => handleTabChange(event, newValue)}
              variant="scrollable"
              scrollButtons={false}
              allowScrollButtonsMobile={false}
              sx={{
                minHeight: 48,
                maxWidth: "100%",
                borderBottom: "1px solid rgba(18, 20, 21, 0.1)",
                "& .MuiTabs-flexContainer": {
                  minWidth: "max-content",
                },
                "& .MuiTabs-scroller": {
                  overflowX: "auto !important",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#1976d2",
                  height: "2px",
                  bottom: 0,
                },
                "& .MuiTab-root": {
                  minWidth: 140,
                  maxWidth: 200,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  borderBottom: "1px solid rgba(18, 20, 21, 0.1)",
                  color: "rgba(0, 0, 0, 0.6)",
                  "&.Mui-selected": {
                    color: "#1976d2",
                    fontWeight: 500,
                    borderBottom: "2px solid #1976d2",
                  },
                  "&:hover": {
                    color: "#1976d2",
                    opacity: 0.8,
                  },
                },
              }}
            >
              {languages.map((lang, idx) => (
                <Tab
                  key={lang.value}
                  label={
                    <span
                      style={{ whiteSpace: "nowrap", textTransform: "none" }}
                    >
                      {lang.value}
                      {lang.value === "en_US" && (
                        <span style={{ fontWeight: 500 }}> (Default)</span>
                      )}
                    </span>
                  }
                  sx={{
                    minWidth: 140,
                    maxWidth: 200,
                    flexShrink: 0,
                    textTransform: "none",
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Right scroll button */}
          {showScrollButtons && (
            <IconButton
              onClick={scrollTabsRight}
              sx={{
                ml: 1,
                minWidth: 32,
                height: 32,
                border: "1px solid #e0e0e0",
                backgroundColor: "#f5f5f5",
                "&:hover": { backgroundColor: "#e0e0e0" },
                flexShrink: 0,
              }}
              size="small"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Add Language Button - Only show when in edit mode */}
        {isEditMode && (
          <Button
            sx={{
              textTransform: "none",
              borderColor: "rgba(42, 44, 45, 1)",
              color: "rgba(42, 44, 45, 1)",
              ml: 2,
              "&:hover": {
                borderColor: "rgba(42, 44, 45, 0.8)",
                backgroundColor: "rgba(42, 44, 45, 0.04)",
              },
            }}
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddLanguageClick}
          >
            Add Language
          </Button>
        )}
      </Box>

      {/* Document display section */}
      {hasDocumentsForCurrentLanguage() ? (
        <>
          {/* Show document forms only if there are documents to display */}
          {getDocumentsToDisplay().length > 0 ? (
            <>
              {/* Column headers */}
              <Box
                sx={{ display: "flex", alignItems: "center", mb: 1, pl: 0.5 }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    width: 220,
                    mr: 2,
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.875rem",
                  }}
                >
                  Document Type
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    width: 260,
                    mr: 2,
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.875rem",
                  }}
                >
                  Document Title
                </Typography>
                {isEditMode && (
                  <Box sx={{ width: 48, ml: 1 }} /> // Spacer for remove button column
                )}
              </Box>

              {getDocumentsToDisplay().map((doc, displayIdx) => (
                <Box
                  key={doc.id || `display_${displayIdx}`}
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <TextField
                    select
                    value={doc.type}
                    onChange={(e) =>
                      handleDocChange(displayIdx, "type", e.target.value)
                    }
                    sx={{ width: 220, mr: 2 }}
                    size="small"
                    disabled={!isEditMode}
                    placeholder="Select document type"
                  >
                    {documentTypes.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    value={doc.title}
                    onChange={(e) =>
                      handleDocChange(displayIdx, "title", e.target.value)
                    }
                    sx={{ width: 260, mr: 2 }}
                    size="small"
                    placeholder="Enter document title"
                    disabled={!isEditMode}
                  />
                  {isEditMode && (
                    <IconButton
                      aria-label="remove document"
                      onClick={() => handleRemoveDoc(displayIdx)}
                      sx={{
                        color: "#d32f2f",
                        border: "1px solid #eee",
                        background: "#fafafa",
                        ml: 1,
                      }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                </Box>
              ))}

              {/* Error message for duplicates */}
              {duplicateError && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: "#ffebee",
                    borderRadius: 1,
                    border: "1px solid #ffcdd2",
                  }}
                >
                  <Typography variant="body2" color="error">
                    {duplicateError}
                  </Typography>
                </Box>
              )}

              {isEditMode && getCurrentLanguageTotalDocumentCount() < 3 && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddDoc}
                  sx={{
                    mb: 3,
                    width: "fit-content",
                    maxWidth: "fit-content",
                    textTransform: "none",
                    borderColor: "#12141599",
                    color: "#12141599",
                    "&:hover": {
                      borderColor: "#12141599",
                      backgroundColor: "rgba(18, 20, 21, 0.04)",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#12141599",
                    },
                  }}
                >
                  Add Document
                </Button>
              )}
            </>
          ) : (
            /* Show "No document added" message and Add Document button when no documents to display */
            <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                No document added for{" "}
                {languages[activeLang]?.value || "this language"}
              </Typography>
              {isEditMode && getCurrentLanguageTotalDocumentCount() < 3 && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddDoc}
                  sx={{
                    width: "fit-content",
                    maxWidth: "fit-content",
                    textTransform: "none",
                    borderColor: "#12141599",
                    color: "#12141599",
                    "&:hover": {
                      borderColor: "#12141599",
                      backgroundColor: "rgba(18, 20, 21, 0.04)",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#12141599",
                    },
                  }}
                >
                  Add Document
                </Button>
              )}
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          <Typography variant="body1">
            No documents available for{" "}
            {languages[activeLang]?.value || "this language"}
          </Typography>
        </Box>
      )}

      {/* Add Language Modal - Only show when in edit mode */}
      {isEditMode && (
        <AddLanguageModal
          open={openAddLanguageModal}
          onClose={handleAddLanguageModalClose}
          onSave={handleAddLanguageSave}
          availableLanguages={locales}
          addRemoveLanguage={addRemoveLanguage}
          currentLanguages={languages}
          currentActiveLanguage={languages[activeLang]?.value || "en_US"}
        />
      )}

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onCancel={() => {
          performCancel();
          setShowUnsavedChangesDialog(false);
        }}
        onSave={handleSaveChanges}
        saveDisabled={checkForDuplicates().hasDuplicates}
      />
    </Box>
  );
}

LegalDocumentationForm.propTypes = {
  locales: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      default: PropTypes.bool,
      clearDocuments: PropTypes.bool,
    })
  ),
  legalDocumentInfos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      enabled: PropTypes.bool,
      documentType: PropTypes.string,
      localizedTitles: PropTypes.object,
    })
  ),
  callLegalDocInfo: PropTypes.func,
};
